/* ============================================================================
   M1 — Courier Partner Dashboard   Route: /supply/dashboard
   v6 §9.5 — D2C supply-side overview: courier partners Prozo works with,
   the delivery modes they cover (forward & reverse) and agreement health.
   Layout:
     • KPI strip (active partners, codes, modes covered, agreements, disputes)
     • Row 1: Delivery-Mode Coverage bars  +  Active Codes by Partner bars
     • Row 2: Forward vs Reverse donut  +  Agreements by Status donut
     • Partner × Delivery-Mode Coverage matrix (only in-use mode columns)
     • Footnote
   Numbers come live from GET courierPartner/parent/dashboard (useDashboardQuery).
   ========= */
import React, {useEffect, useMemo, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined'
import SettingsInputComponentOutlinedIcon from '@mui/icons-material/SettingsInputComponentOutlined'
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined'
import PropTypes from 'prop-types'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import PageTitle from '../../../components/PageTitle'
import {KpiStrip, initials, useToast, SupplyHero, HERO_BTN, HERO_BTN_SOLID} from '../components'
import {useDashboardQuery, useListParentsQuery} from '../../../redux/supplyApiSlice'
import {Box, Stack, Button, Skeleton, Typography, ToggleButtonGroup, ToggleButton, Table, TableBody, TableCell, TableHead, TableRow} from '@mui/material'
import {alpha} from '@mui/material/styles'
import {Donut, MiniBar} from './charts'

/* ───────── Delivery-mode taxonomy (mirrors mockup DMODES + serviceTypes.js) ───────── */
const DMODES = [
  {key: 'Surface',        abbr: 'Surface',  color: '#6366F1', re: /^FWD_SURFACE/},
  {key: 'Air',            abbr: 'Air',      color: '#1976D2', re: /^FWD_AIR/},
  {key: 'Same-Day (SDD)', abbr: 'SDD',      color: '#0F766E', re: /^SDD_/},
  {key: 'Same-City NDD',  abbr: 'SC-NDD',   color: '#00897B', re: /^SC_NDD/},
  {key: 'Intercity NDD',  abbr: 'IC-NDD',   color: '#2E7D32', re: /^INTERCITY_NDD/},
  {key: '2–4 hour',       abbr: '2–4h',     color: '#F57C00', re: /^(H2TO4|SUB4H)/},
  {key: 'Sub-2 hour',     abbr: '<2h',      color: '#E65100', re: /^SUB2H/},
  {key: 'High Value',     abbr: 'HV',       color: '#6A1B9A', re: /^HV_/},
  {key: 'Reverse (RVP)',  abbr: 'RVP',      color: '#8D6E63', re: /^REV_/},
]

const stIsReverse = (st) => /^REV_/.test(st) || /^HV_REVERSE/.test(st)

/* deterministic avatar color per partner name (backend has no color field) */
const PALETTE = ['#6366F1', '#1976D2', '#0F766E', '#2E7D32', '#F57C00', '#6A1B9A', '#00897B', '#B45309', '#1565C0', '#8D6E63']
const colorFor = (name) => {
  let h = 0
  const s = String(name || '')
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0
  return PALETTE[Math.abs(h) % PALETTE.length]
}

/* small round initials avatar */
function Avi({name, size = 22}) {
  return (
    <Box sx={{width: size, height: size, borderRadius: '50%', flexShrink: 0, bgcolor: colorFor(name), color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: size <= 22 ? 9 : 11, fontWeight: 700}}>
      {initials(name)}
    </Box>
  )
}
Avi.propTypes = {name: PropTypes.string, size: PropTypes.number}

/* premium section panel (header + body) */
function Panel({title, caption, action, children, sx}) {
  return (
    <Box sx={{border: 1, borderColor: 'divider', borderRadius: 3, bgcolor: 'background.paper', boxShadow: '0 1px 2px rgba(15,23,42,.04)', display: 'flex', flexDirection: 'column', overflow: 'hidden', ...sx}}>
      <Stack direction='row' justifyContent='space-between' alignItems='center' flexWrap='wrap' useFlexGap spacing={1} sx={{px: 2.5, py: 1.75, borderBottom: 1, borderColor: 'divider'}}>
        <Box sx={{minWidth: 0}}>
          <Typography sx={{fontSize: 14, fontWeight: 700, color: 'text.primary'}}>{title}</Typography>
          {caption ? <Typography sx={{fontSize: 12, color: 'text.secondary', mt: '2px'}}>{caption}</Typography> : null}
        </Box>
        {action || null}
      </Stack>
      <Box sx={{p: 2.5, flex: 1}}>{children}</Box>
    </Box>
  )
}
Panel.propTypes = {title: PropTypes.node, caption: PropTypes.node, action: PropTypes.node, children: PropTypes.node, sx: PropTypes.object}

const HERO_ACTIONS = (navigate) => (
  <>
    <Button variant='outlined' startIcon={<AddCircleOutlineIcon />} onClick={() => navigate('/supply/partners/onboard')} sx={HERO_BTN}>
      Onboard Partner
    </Button>
    <Button variant='contained' disableElevation endIcon={<ArrowForwardIcon />} onClick={() => navigate('/supply/partners')} sx={HERO_BTN_SOLID}>
      Partner Directory
    </Button>
  </>
)

/* ════════════════════ PAGE ════════════════════ */
export default function Dashboards() {
  const navigate = useNavigate()
  const toast = useToast()
  const [dir, setDir] = useState('All') // Delivery-Mode Coverage direction filter (All / Forward / Reverse)

  /* ── live dashboard data (RTK Query — GET courierPartner/parent/dashboard) ── */
  const {data: dash = {}, isFetching: loading, isError, error: errObj, refetch: load} = useDashboardQuery()
  /* DRAFT parents = onboardings started but not finished — surfaced as a resume banner */
  const {data: parentList} = useListParentsQuery()
  const drafts = useMemo(
    () => (parentList || []).filter((p) => p && String(p.status).toUpperCase() === 'DRAFT'),
    [parentList]
  )
  const error = isError ? (errObj?.message || 'Failed to load dashboard') : null
  useEffect(() => {
    if (isError) toast(error, 'error')
  }, [isError, error, toast])

  const {perParent, kpiData, fwd, rev, activeModes, partnerBarRows, matrixRows} =
    useMemo(() => {
      const parents    = (dash && dash.parents)    || {}
      const codesAgg   = (dash && dash.codes)      || {}
      const agreements = (dash && dash.agreements) || {}
      const perParent  = (dash && dash.perParent)  || []

      /* KPI counts */
      const activeParts  = parents.active || 0
      const parentsTotal = parents.total || 0
      const codesTotal   = codesAgg.total || 0
      const activeCodes  = codesAgg.active || 0
      const draftCodes   = codesTotal - activeCodes
      const signed       = agreements.signed || 0
      const pending      = agreements.pendingSigning || 0
      const pendingApproval = agreements.pendingApproval || 0
      const superseded   = agreements.superseded || 0

      /* Forward / Reverse counts (per-parent service-type offerings) */
      let fwd = 0, rev = 0
      perParent.forEach((p) =>
        (p.serviceTypes || []).forEach((st) => {
          if (stIsReverse(st)) rev++
          else fwd++
        })
      )

      /* Active delivery modes (only those at least one partner covers) */
      const activeModes = DMODES.filter((m) =>
        perParent.some((p) => (p.serviceTypes || []).some((st) => m.re.test(st)))
      )
      const modesCovered = activeModes.length

      /* Active Codes by Partner bars */
      const withCodes = perParent.filter((p) => (p.totalCodes || 0) > 0)
      const maxTot = Math.max(1, ...withCodes.map((p) => p.totalCodes || 0))
      const partnerBarRows = withCodes.map((p) => {
        const act   = p.activeCodes || 0
        const tot   = p.totalCodes || 0
        const inact = tot - act
        return {id: p.parentId, name: p.parentName, color: colorFor(p.parentName), act, inact, tot, maxTot}
      })

      /* Matrix rows (parents that have at least one code) */
      const matrixRows = withCodes.map((p) => {
        const sts = p.serviceTypes || []
        const cells = activeModes.map((m) => {
          const n = sts.some((st) => m.re.test(st)) ? 1 : 0
          return {mode: m, n}
        })
        const modeCount = cells.filter((cell) => cell.n > 0).length
        return {
          p: {id: p.parentId, name: p.parentName, color: colorFor(p.parentName)},
          cells,
          modeCount,
        }
      })

      const kpiData = {
        activeParts, parentsTotal, codesTotal, activeCodes, draftCodes,
        modesCovered, signed, pending, pendingApproval, superseded,
      }

      return {perParent, kpiData, fwd, rev, activeModes, partnerBarRows, matrixRows}
    }, [dash])

  /* ── KPI cards ── */
  const coverage = (dash && dash.coverage) || {}
  const kpis = [
    {
      label: 'Active Partners',
      value: kpiData.activeParts,
      icon: <StorefrontOutlinedIcon />,
      accent: '#6366F1',
      sub: 'of ' + kpiData.parentsTotal + ' D2C courier partners',
    },
    {
      label: 'Courier Codes',
      value: kpiData.codesTotal,
      icon: <SettingsInputComponentOutlinedIcon />,
      accent: '#1976D2',
      sub: kpiData.activeCodes + ' active · ' + kpiData.draftCodes + ' draft',
    },
    {
      label: 'Delivery Modes Covered',
      value: kpiData.modesCovered,
      icon: <LocalShippingOutlinedIcon />,
      accent: '#0F766E',
      sub: 'of ' + DMODES.length + ' delivery modes',
    },
    {
      label: 'Agreements Signed',
      value: kpiData.signed,
      icon: <DescriptionOutlinedIcon />,
      accent: '#2E7D32',
      sub: coverage.totalCodes
        ? coverage.governedCodes + '/' + coverage.totalCodes + ' codes governed (' + (coverage.pct || 0) + '%)'
        : 'across D2C partners',
    },
    {
      label: 'Pending Signing',
      value: kpiData.pending,
      icon: <AccessTimeOutlinedIcon />,
      accent: '#F57C00',
      sub: 'addendums awaiting signature',
    },
    {
      label: 'Open Disputes',
      value: <span title='Not yet tracked'>—</span>,
      icon: <WarningAmberOutlinedIcon />,
      accent: '#6B7280',
      sub: 'not yet tracked',
    },
  ]

  /* ── donut data ── */
  const dirDonut = [
    {label: 'Forward',       value: fwd, color: '#6366F1'},
    {label: 'Reverse (RVP)', value: rev, color: '#F57C00'},
  ]
  const agrDonut = [
    {label: 'Signed (governing)', value: kpiData.signed,          color: '#2E7D32'},
    {label: 'Pending signing',    value: kpiData.pending,         color: '#F57C00'},
    {label: 'Pending approval',   value: kpiData.pendingApproval, color: '#1976D2'},
    {label: 'Superseded',         value: kpiData.superseded,      color: '#8D6E63'},
  ]

  const isEmpty = perParent.length === 0

  if (loading || error) {
    return (
      <>
        <PageTitle>Courier Partner Dashboard — ProShip</PageTitle>
        <SupplyHero
          icon={<DashboardOutlinedIcon />}
          title='Courier Partner Dashboard'
          subtitle='D2C Freight — the courier partners Prozo works with, the delivery modes they cover (forward & reverse) and agreement health.'
          actions={HERO_ACTIONS(navigate)}
        />
        {loading ? (
          <DashboardSkeleton />
        ) : (
          <Box sx={{border: 1, borderColor: (th) => alpha(th.palette.error.main, 0.3), bgcolor: (th) => alpha(th.palette.error.main, 0.05), borderRadius: 3, p: 2.5, display: 'flex', alignItems: 'center', gap: 1.5, color: 'error.dark'}}>
            <WarningAmberOutlinedIcon sx={{fontSize: 20, flexShrink: 0}} />
            <Typography sx={{flex: 1, fontSize: 13}}>Failed to load dashboard: {error}</Typography>
            <Button variant='outlined' color='error' size='small' onClick={load} sx={{textTransform: 'none', fontWeight: 600, borderRadius: '10px'}}>Retry</Button>
          </Box>
        )}
      </>
    )
  }

  return (
    <>
      <PageTitle>Courier Partner Dashboard — ProShip</PageTitle>
      <SupplyHero
        icon={<DashboardOutlinedIcon />}
        title='Courier Partner Dashboard'
        subtitle='D2C Freight — the courier partners Prozo works with, the delivery modes they cover (forward & reverse) and agreement health.'
        actions={HERO_ACTIONS(navigate)}
      />

      <KpiStrip cards={kpis} />

      {drafts.length ? (
        <Box sx={{mb: 2, p: 2, borderRadius: 3, border: 1, borderColor: (th) => alpha(th.palette.warning.main, 0.4), borderLeft: '4px solid #D97706', bgcolor: (th) => alpha(th.palette.warning.main, 0.07)}}>
          <Typography sx={{fontWeight: 800, fontSize: 14, color: 'text.primary'}}>
            {drafts.length} onboarding{drafts.length > 1 ? 's' : ''} to finish
          </Typography>
          <Typography sx={{fontSize: 12, color: 'text.secondary', mt: 0.25, mb: 1.5}}>
            These courier partners were started but not completed. Resume to finish and activate them.
          </Typography>
          <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
            {drafts.map((p) => (
              <Button
                key={p.id}
                variant='outlined'
                size='small'
                endIcon={<ArrowForwardIcon style={{fontSize: 14}} />}
                onClick={() => navigate('/supply/partners/onboard?resume=' + encodeURIComponent(p.id))}
                title={'Resume onboarding for ' + (p.name || p.id)}
                sx={{textTransform: 'none', fontWeight: 600, borderRadius: '10px', borderStyle: 'dashed', borderColor: '#D97706', color: '#B45309', '&:hover': {borderColor: '#B45309', bgcolor: (th) => alpha(th.palette.warning.main, 0.08)}}}
              >
                {p.name || p.legalEntityName || p.id} · Resume
              </Button>
            ))}
          </Stack>
        </Box>
      ) : null}

      {isEmpty ? (
        <Box sx={{mt: 2}}>
          <EmptyDashboard navigate={navigate} />
        </Box>
      ) : (
        <>
          {/* ───── Row 1: bar charts paired ───── */}
          <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', md: '1fr 1fr'}, gap: 2, mb: 2}}>
            <Panel
              title='Delivery-Mode Coverage'
              action={
                <ToggleButtonGroup
                  size='small'
                  exclusive
                  value={dir}
                  onChange={(e, val) => val && setDir(val)}
                  sx={{'& .MuiToggleButton-root': {textTransform: 'none', fontWeight: 600, fontSize: 12, px: 1.5, py: 0.4}}}
                >
                  {['All', 'Forward', 'Reverse'].map((d) => (
                    <ToggleButton key={d} value={d}>{d}</ToggleButton>
                  ))}
                </ToggleButtonGroup>
              }
            >
              <DeliveryModeBars parents={perParent} dir={dir} />
            </Panel>

            <Panel title='Active Codes by Partner' caption='active vs total account codes'>
              <Stack spacing={1.5}>
                {partnerBarRows.map((r) => (
                  <Box key={r.id} sx={{display: 'grid', gridTemplateColumns: '150px 1fr 60px', alignItems: 'center', gap: 1.5}}>
                    <Stack direction='row' spacing={1} alignItems='center' sx={{minWidth: 0}}>
                      <Avi name={r.name} size={20} />
                      <Typography sx={{fontSize: 12.5, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{r.name}</Typography>
                    </Stack>
                    <MiniBar
                      height={11}
                      segments={[
                        {label: r.act + ' active code(s)', value: r.act, color: '#2E7D32'},
                        {label: r.inact + ' draft / inactive code(s)', value: r.inact, color: '#CBD5E1'},
                      ]}
                    />
                    <Box sx={{textAlign: 'right', fontWeight: 700, fontSize: 12.5}}>
                      <Box component='span' sx={{color: '#2E7D32'}}>{r.act}</Box>
                      <Box component='span' sx={{color: 'text.disabled'}}>/{r.tot}</Box>
                    </Box>
                  </Box>
                ))}
              </Stack>
            </Panel>
          </Box>

          {/* ───── Row 2: two donuts paired ───── */}
          <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', md: '1fr 1fr'}, gap: 2}}>
            <Panel title='Forward vs Reverse' caption='service-type offerings'>
              <Donut segments={dirDonut} unit='service types' />
              <Typography sx={{fontSize: 12, color: 'text.secondary', textAlign: 'center', mt: 1.5}}>
                <b>{fwd}</b> forward · <b>{rev}</b> reverse service types across <b>{perParent.length}</b> D2C partners
              </Typography>
            </Panel>

            <Panel title='Agreements by Status' caption='D2C partners'>
              <Donut segments={agrDonut} unit='agreements' />
              <Typography sx={{fontSize: 12, color: 'text.secondary', textAlign: 'center', mt: 1.5}}>
                <b>{kpiData.signed}</b> signed &amp; governing · <b>{kpiData.pending}</b> awaiting signature
              </Typography>
            </Panel>
          </Box>

          {/* ───── Partner × Delivery-Mode Coverage matrix ───── */}
          <MatrixCard matrixRows={matrixRows} activeModes={activeModes} />

          {/* ───── footnote ───── */}
          <Box sx={{mt: 2, p: 2, borderRadius: 3, border: 1, borderColor: 'divider', bgcolor: (th) => alpha(th.palette.primary.main, 0.03), display: 'flex', alignItems: 'flex-start', gap: 1.5, color: 'text.secondary'}}>
            <InfoOutlinedIcon sx={{fontSize: 18, flexShrink: 0, mt: '1px', color: 'primary.main'}} />
            <Typography sx={{fontSize: 12.5, lineHeight: 1.55}}>
              Delivery modes mirror the serviceability service-type taxonomy — Surface, Air, Same-Day (SDD), Same-City &amp;
              Intercity NDD, 2–4 hr &amp; Sub-2 hr hyperlocal, High Value and Reverse. Counts reflect configured D2C account
              codes; PTL partners live in a separate module.
            </Typography>
          </Box>
        </>
      )}
    </>
  )
}

/* ────────── Loading skeleton ────────── */
function DashboardSkeleton() {
  return (
    <Box>
      <Box sx={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 1.5, mb: 2}}>
        {Array.from({length: 6}).map((_, i) => (
          <Skeleton key={i} variant='rounded' height={98} sx={{borderRadius: '14px'}} />
        ))}
      </Box>
      <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', md: '1fr 1fr'}, gap: 2, mb: 2}}>
        <Skeleton variant='rounded' height={300} sx={{borderRadius: '14px'}} />
        <Skeleton variant='rounded' height={300} sx={{borderRadius: '14px'}} />
      </Box>
      <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', md: '1fr 1fr'}, gap: 2}}>
        <Skeleton variant='rounded' height={240} sx={{borderRadius: '14px'}} />
        <Skeleton variant='rounded' height={240} sx={{borderRadius: '14px'}} />
      </Box>
    </Box>
  )
}

/* ────────── Empty state (no courier data yet) ────────── */
function EmptyDashboard({navigate}) {
  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: 1,
        borderColor: 'divider',
        borderRadius: 3,
        p: {xs: 4, md: 7},
        textAlign: 'center',
      }}
    >
      <Box
        sx={{
          width: 64,
          height: 64,
          borderRadius: '18px',
          mx: 'auto',
          mb: 2,
          bgcolor: 'primary.lighter',
          color: 'primary.main',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <StorefrontOutlinedIcon sx={{fontSize: 32}} />
      </Box>
      <Typography sx={{fontWeight: 700, fontSize: 18, color: 'text.primary'}}>No courier data yet</Typography>
      <Typography sx={{color: 'text.secondary', fontSize: 14, mt: 1, maxWidth: 460, mx: 'auto', lineHeight: 1.5}}>
        Once you onboard courier partners and add account codes, their delivery-mode coverage, agreement health and
        activity will show up here.
      </Typography>
      <Stack direction={{xs: 'column', sm: 'row'}} spacing={1.5} justifyContent='center' sx={{mt: 3}}>
        <Button
          variant='contained'
          startIcon={<AddCircleOutlineIcon />}
          onClick={() => navigate('/supply/partners/onboard')}
          sx={{borderRadius: '12px', px: 2.5}}
        >
          Onboard Partner
        </Button>
        <Button variant='outlined' onClick={() => navigate('/supply/partners')} sx={{borderRadius: '12px', px: 2.5}}>
          Partner Directory
        </Button>
      </Stack>
    </Box>
  )
}

EmptyDashboard.propTypes = {navigate: PropTypes.func.isRequired}

/* ────────────────────────────────────────────────────────────────────────────
   DeliveryModeBars — one mini-bar per delivery mode (all 9 DMODES): # partners
   that cover it relative to the max across modes, filtered by the All / Forward
   / Reverse toggle. Modes with no coverage under the current filter show "none".
   ──────────────────────────────────────────────────────────────────────────── */
function DeliveryModeBars({parents, dir}) {
  const rows = DMODES.map((m) => {
    let p = 0
    parents.forEach((pp) => {
      const hit = (pp.serviceTypes || []).some(
        (st) =>
          m.re.test(st) &&
          (dir === 'All' || (dir === 'Reverse' ? stIsReverse(st) : !stIsReverse(st)))
      )
      if (hit) p++
    })
    return {m, p}
  })
  const maxP = Math.max(1, ...rows.map((r) => r.p))
  return (
    <Stack spacing={1.5}>
      {rows.map((r) => (
        <Box key={r.m.key} sx={{display: 'grid', gridTemplateColumns: '128px 1fr 92px', alignItems: 'center', gap: 1.5}}>
          <Typography sx={{fontSize: 12.5, fontWeight: 700, color: 'text.primary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}} title={r.m.key}>
            {r.m.key}
          </Typography>
          <MiniBar
            height={11}
            segments={[
              {label: r.p + ' partner(s)', value: r.p, color: r.m.color},
              {label: '', value: maxP - r.p, color: 'transparent'},
            ]}
          />
          <Box sx={{textAlign: 'right', fontSize: 12, fontWeight: 700}} title={r.p + ' partner(s)'}>
            {r.p ? (
              <>
                {r.p}
                <Box component='span' sx={{color: 'text.disabled', fontWeight: 500}}> partner(s)</Box>
              </>
            ) : (
              <Box component='span' sx={{color: 'text.disabled', fontWeight: 500}}>none</Box>
            )}
          </Box>
        </Box>
      ))}
    </Stack>
  )
}
DeliveryModeBars.propTypes = {parents: PropTypes.array.isRequired, dir: PropTypes.string.isRequired}

/* ────────────────────────────────────────────────────────────────────────────
   MatrixCard — Partner × Delivery-Mode Coverage
   Only renders delivery-mode columns that at least one D2C code actually covers.
   ──────────────────────────────────────────────────────────────────────────── */
function MatrixCard({matrixRows, activeModes}) {
  if (!matrixRows.length) return null
  const HEAD = {fontSize: 11, fontWeight: 700, color: 'text.secondary', borderBottom: 1, borderColor: 'divider', py: 1.25}
  const CELL = {fontSize: 13, borderBottom: 1, borderColor: 'divider', py: 1}
  return (
    <Panel title='Partner × Delivery-Mode Coverage' caption='which couriers support which delivery modes (D2C account codes)' sx={{mt: 2}}>
      <Box sx={{overflowX: 'auto'}}>
        <Table size='small' sx={{minWidth: 640, '& td, & th': {borderColor: 'divider'}}}>
          <TableHead>
            <TableRow>
              <TableCell sx={HEAD}>Parent Courier Partner</TableCell>
              {activeModes.map((m) => (
                <TableCell key={m.key} align='center' sx={{...HEAD, whiteSpace: 'nowrap'}} title={m.key}>{m.abbr}</TableCell>
              ))}
              <TableCell align='center' sx={HEAD}>Modes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {matrixRows.map(({p, cells, modeCount}) => (
              <TableRow key={p.id} hover>
                <TableCell sx={CELL}>
                  <Stack direction='row' spacing={1} alignItems='center'>
                    <Avi name={p.name} size={22} />
                    <Typography sx={{fontSize: 13, fontWeight: 700}}>{p.name}</Typography>
                  </Stack>
                </TableCell>
                {cells.map(({mode, n}) => (
                  <TableCell key={mode.key} align='center' sx={CELL}>
                    {n ? (
                      <Box
                        component='span'
                        title={p.name + ' covers ' + mode.key}
                        sx={{display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: '7px', fontSize: 12, fontWeight: 700, bgcolor: alpha(mode.color, 0.12), color: mode.color}}
                      >
                        ✓
                      </Box>
                    ) : (
                      <Box component='span' sx={{color: 'text.disabled'}}>—</Box>
                    )}
                  </TableCell>
                ))}
                <TableCell align='center' sx={{...CELL, fontWeight: 700}}>{modeCount}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Panel>
  )
}
MatrixCard.propTypes = {
  matrixRows: PropTypes.array.isRequired,
  activeModes: PropTypes.array.isRequired,
}
